import { Box, Icon } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import type { ServicesApiEnvHealthResponse } from '@/services/api/services-api';
import { delayStatus, HIGH_IMPACT_CATEGORIES } from '../utils';
import * as styles from './health-score-gauge.css';

const SIZE = 84;
const STROKE = 6;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const SCORE_COLORS: Record<string, { arc: string; track: string }> = {
  green: {
    arc: themeVars.palette['green-200'],
    track: themeVars.color['border-minimal'],
  },
  yellow: {
    arc: themeVars.palette['yellow-200'],
    track: themeVars.color['border-minimal'],
  },
  red: {
    arc: themeVars.palette['red-200'],
    track: themeVars.color['border-base'],
  },
};

const SCORE_ICON: Record<string, { name: 'check-circle' | 'warning'; color: 'success' | 'caution' | 'critical' }> = {
  green: { name: 'check-circle', color: 'success' },
  yellow: { name: 'warning', color: 'caution' },
  red: { name: 'warning', color: 'critical' },
};

function scoreColor(score: number): string {
  if (score >= 80) return 'green';
  if (score >= 30) return 'yellow';
  return 'red';
}

function computeHealthScore(data: ServicesApiEnvHealthResponse): number {
  const allCategories = [...data.processing.categories, ...data.egress.categories];

  if (allCategories.length === 0) return 100;

  let penalty = 0;
  let lowImpactPenalty = 0;

  for (const cat of allCategories) {
    const status = delayStatus(cat.delay_seconds, cat.pending);
    if (status.color === 'red') {
      penalty += 25;
    } else if (status.color === 'yellow') {
      if (HIGH_IMPACT_CATEGORIES.has(cat.name)) {
        penalty += 10;
      } else {
        lowImpactPenalty += 10;
      }
    }
  }

  penalty += Math.min(lowImpactPenalty, 15);

  if (data.tasks.queued_count > 0) {
    penalty += Math.min(data.tasks.queued_count * 2, 10);
  }

  if (data.tasks.running != null) {
    penalty += 5;
  }

  return Math.max(0, Math.min(100, 100 - penalty));
}

interface HealthScoreGaugeProps {
  data: ServicesApiEnvHealthResponse;
}

export function HealthScoreGauge({ data }: HealthScoreGaugeProps) {
  const score = computeHealthScore(data);
  const color = scoreColor(score);
  const colors = SCORE_COLORS[color];
  const icon = SCORE_ICON[color];
  const isDegraded = data.health === 'degraded';
  const busyness = 1 - score / 100;
  const dashOffset = isDegraded ? 0 : CIRCUMFERENCE * (1 - busyness);
  const arcColor = colors.arc;
  const trackColor = isDegraded ? colors.arc : colors.track;
  const center = SIZE / 2;

  return (
    <Box className={styles.gaugeContainer} style={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        {/* Track circle */}
        <circle cx={center} cy={center} r={RADIUS} fill="none" stroke={trackColor} strokeWidth={STROKE} />
        {/* Progress arc */}
        {!isDegraded ? (
          <circle
            cx={center}
            cy={center}
            r={RADIUS}
            fill="none"
            stroke={arcColor}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${center} ${center})`}
          />
        ) : null}
      </svg>
      <Box className={styles.gaugeLabel}>
        <Icon name={icon.name} size="lg" color={icon.color} />
      </Box>
    </Box>
  );
}
